import React, { useCallback } from 'react'
import useAppDispatch from '../../../hooks/useAppDispatch'
import useAppSelector from '../../../hooks/useAppSelector'
import { Dialog } from '@mui/material'
import { QrCodeReader } from './QrCodeReader'
import {
  QrContents,
  CheckinEntry,
  QRCodePayload,
  DecodedQrCode
} from '../../../app/types'
import { setSnackbarMessages } from '../../snackbar/snackbarSlice'
import {
  setSelectedEntry,
  getQRPausedSelector,
  setQRPaused,
  setDirtyBib,
} from '../../checkin/checkinSlice'
import { getHasCameraAccessSelector } from '../../camera/cameraSlice'
import { extractBibValue } from '../../../utils/extractBibValue'
import { createLogger } from '../../../app/logger'
import {
  parseJson,
  doAtob
} from '../../../utils/decoder'

const logger = createLogger({
  prefix: 'QrComponent'
})

const QrComponent = ({
  entries = []
}: {
  entries: CheckinEntry[]
}) => {
  const dispatch = useAppDispatch()
  const paused = useAppSelector(getQRPausedSelector)
  const [qrResult, setQRResult] = React.useState('')
  const hasCameraAccess = useAppSelector(getHasCameraAccessSelector)

  const handleScan = (result?: QrContents) => {
    if (result) {
      logger.info('scanning result', result)
      dispatch(setQRPaused(true))
      if (result.text.includes('/bib/')) {
        const bibValue = extractBibValue(result.text)
        setQRResult(bibValue)
      } else {
        setQRResult(result.text)
      }
    }
  }

  const decodeQrCode = (raw: string): DecodedQrCode => {
    // CASE 1: if this can be parsed as number, that means it is a bib
    if (Number.isInteger(+raw)) {
      return {
        type: 'bib',
        payload: {
          bib: raw.toString()
        },
        status: true
      }
    }

    // CASE 2: if we can find a match, that means it is URL linking to results, with bib as a param
    const regex = /^(?:\d+$|.*\b[Bb][Ii][Bb][^\d]*(\d+).*)$/
    const matches = raw.match(regex)

    if (matches && matches.length > 1) {
      const bib = matches[1]
      return {
        type: 'bib',
        payload: {
          bib
        },
        status: true
      }
    }
    // CASE 3: JSON containing id
    const atobed = doAtob(raw)
    if (!atobed && raw) {
      return {
        type: 'external_id',
        status: true,
        payload: {
          externalId: raw
        }
      }
    }
    // qr code from confirmation email, presented as JSON
    const payload = parseJson(atobed)

    // if payload is valid, this means it is JSON object containing entryId or transId
    if (payload) {
      return {
        type: 'reg_confirm',
        payload,
        status: true
      }
    }

    // if this line is reached that means QR code is not decoded properly
    // or contains unknown data
    dispatch(setSnackbarMessages({
      messageLines: [
        {
          message: 'Unable to decode QR code'
        }
      ],
      severity: 'error'
    }))

    return {
      status: false,
      payload: {}
    }
  }

  const handleQRCodeForRegConfirm = (payload: QRCodePayload) => {
    // safeguard, because we sent some emails with encoded transaction id.
    // this was before we decided to use entry id
    const prop = Object.prototype.hasOwnProperty.call(payload, 'transId')
      ? 'transId'
      : 'entryId'

    const propId = payload[prop]
    const entry = entries.find(oneEntry => propId ? oneEntry[prop] === +propId : false)

    if (entry) {
      dispatch(setSelectedEntry(entry))
    } else {
      dispatch(setSnackbarMessages({
        messageLines: [
          {
            message: 'Athlete is not found in this event'
          }
        ],
        severity: 'error'
      }))
    }
  }

  const handleQRCodeForExternalId = (payload: QRCodePayload) => {
    const entry = entries.find(oneEntry => oneEntry.entryExternalId === payload.externalId)

    if (entry) {
      dispatch(setSelectedEntry(entry))
    } else {
      dispatch(setSnackbarMessages({
        messageLines: [
          {
            message: 'Athlete is not found in this event'
          }
        ],
        severity: 'error'
      }))
    }
  }

  const decodeQrCodeCallback = useCallback(decodeQrCode, [dispatch])
  const handleQRCodeForRegConfirmCallback = useCallback(handleQRCodeForRegConfirm, [dispatch, entries])
  const handleQRCodeForExternalIdCallback = useCallback(handleQRCodeForExternalId, [dispatch, entries])

  function closeQR() {
    dispatch(setQRPaused(true))
  }

  React.useEffect(() => {
    if (qrResult) {
      const decoded = decodeQrCodeCallback(qrResult)
      if (decoded.status) {
        switch (decoded.type) {
          case 'reg_confirm': {
            handleQRCodeForRegConfirmCallback(decoded.payload as QRCodePayload)
            break
          }
          case 'bib': {
            const bibPayload: QRCodePayload = decoded.payload
            dispatch(setDirtyBib(bibPayload.bib))
            break
          }
          case 'external_id': {
            handleQRCodeForExternalIdCallback(decoded.payload as QRCodePayload)
            break
          }
        }
      }

      dispatch(setQRPaused(true))
      setQRResult('')
    }
  }, [qrResult, dispatch, entries, handleQRCodeForRegConfirmCallback, decodeQrCodeCallback, handleQRCodeForExternalIdCallback])

  return (
    <Dialog
      open={!paused && hasCameraAccess!}
      onClose={closeQR}
    >
      <QrCodeReader
        handleScan={handleScan}
      />
    </Dialog>
  )
}

export default QrComponent
